import { useEffect, useRef } from 'react';
import {
  Leaf, User, Calendar, Clock, Globe, Lightbulb,
  RefreshCw, Star, Key, Cloud, Clipboard, Target, Users, Zap,
} from 'lucide-react';

const EVENT_DATE = 'Thursday, 9 July 2026';

function GreenCheck() {
  return (
    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sage-700 flex items-center justify-center mt-0.5">
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function GreenIconCircle({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-center justify-center w-12 h-12 rounded-full bg-sage-100 border border-sage-300 text-sage-700">
      {children}
    </span>
  );
}

function GHLForm() {
  useEffect(() => {
    if (document.querySelector('script[src="https://link.msgsndr.com/js/form_embed.js"]')) return;
    const script = document.createElement('script');
    script.src = 'https://link.msgsndr.com/js/form_embed.js';
    document.body.appendChild(script);
  }, []);

  return (
    <iframe
      src="https://api.leadconnectorhq.com/widget/form/Jho49K7xowqEzH1Jl3aj"
      style={{ width: '100%', height: '511px', border: 'none', borderRadius: '8px' }}
      id="inline-Jho49K7xowqEzH1Jl3aj"
      data-layout='{"id":"INLINE"}'
      data-trigger-type="alwaysShow"
      data-trigger-value=""
      data-activation-type="alwaysActivated"
      data-activation-value=""
      data-deactivation-type="neverDeactivate"
      data-deactivation-value=""
      data-form-name="Coaching Plateau"
      data-height="511"
      data-layout-iframe-id="inline-Jho49K7xowqEzH1Jl3aj"
      data-form-id="Jho49K7xowqEzH1Jl3aj"
      title="Coaching Plateau"
    />
  );
}

export function CoachingPlateauPage() {
  const formRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="font-body" style={{ paddingBottom: '80px' }}>

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-chocolate-900" style={{ minHeight: '520px' }}>
        {/* Stage image — fills right two-thirds, speaker visible */}
        <div className="absolute inset-y-0 right-0 w-2/3 pointer-events-none select-none">
          <img
            src="/Patrick_Stage_Image.png"
            alt=""
            className="w-full h-full object-cover object-center"
          />
          {/* Thin left-edge fade only — image already has dark left built in */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to right, #3D2B20 0%, transparent 30%)' }}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to bottom, transparent 70%, #3D2B20 100%)' }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-14">
          <div className="max-w-lg">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sage-700 bg-sage-900/20 text-white text-xs font-semibold mb-6 tracking-wide uppercase">
              <Leaf size={13} className="text-sage-400" />
              Free 90-Minute Live Training
            </div>

            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl font-extrabold text-white leading-[1.05] mb-3 tracking-tight">
              The Coaching Plateau
            </h1>
            <div className="w-10 h-0.5 bg-sage-600 mb-5" />

            {/* Subtitle */}
            <h2 className="text-2xl sm:text-[28px] font-bold text-white leading-snug mb-5">
              Why <span className="text-sage-400">Good Coaches Stay Stuck</span> —<br />
              and What Actually Breaks Them Through
            </h2>

            {/* Body */}
            <p className="text-sm text-chocolate-200 leading-relaxed mb-8 max-w-md">
              A free 90-minute live training for coaches, NLP practitioners,
              hypnotherapists, consultants, trainers, and wellness professionals
              who are doing good work — but feel plateaued, overwhelmed,
              or unable to break through to the next level.
            </p>

            {/* Event detail pills */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-chocolate-800 border border-chocolate-700 text-white text-sm">
                <Calendar size={14} className="text-sage-400 flex-shrink-0" />
                {EVENT_DATE}
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-chocolate-800 border border-chocolate-700 text-white text-sm">
                <Clock size={14} className="text-sage-400 flex-shrink-0" />
                10:00 AM – 11:30 AM Eastern
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-chocolate-800 border border-chocolate-700 text-white text-sm">
                <Globe size={14} className="text-sage-400 flex-shrink-0" />
                3:00 PM – 4:30 PM UK &amp; Europe
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={scrollToForm}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-sage-700 hover:bg-sage-800 text-white font-semibold text-base transition-colors"
            >
              Save My Free Seat →
            </button>
          </div>
        </div>
      </section>

      {/* ── YOU'RE A GOOD COACH (cream) ───────────────────────── */}
      <section className="bg-cream-300">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="grid md:grid-cols-2 gap-8 md:gap-0">
            {/* Left */}
            <div className="md:pr-10 pb-8 md:pb-0 border-b md:border-b-0 md:border-r border-cream-500">
              <GreenIconCircle>
                <User size={22} />
              </GreenIconCircle>
              <h2 className="text-xl font-bold text-chocolate-900 mt-4 mb-4">
                You're a good coach.
              </h2>
              <div className="space-y-1 text-sm text-chocolate-700">
                <p>You know your craft.</p>
                <p>You care about your clients.</p>
                <p>You've invested in your development.</p>
                <p>And you've helped people create real change.</p>
              </div>
              <p className="mt-5 font-bold text-sm text-chocolate-900 leading-snug">
                But something still isn't moving in your own business, income, or growth.
              </p>
            </div>

            {/* Right */}
            <div className="md:pl-10">
              <h2 className="text-xl font-bold text-chocolate-900 mb-5">You may have:</h2>
              <ul className="space-y-3">
                {[
                  'More certifications than you can count — but still feel like something important is missing',
                  'Skill and experience — but not enough consistent clients, income, or momentum',
                  'Tried marketing harder, posting more, launching more, or "being more visible" — only to feel the same stuck pattern return',
                  'Created success before — but watched the momentum fade, dip, or disappear again',
                  'Seen less qualified coaches move faster than you, and wondered what they are doing differently',
                  "Reached a ceiling you can't seem to push through, no matter how much you learn or how much effort you put in",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <GreenCheck />
                    <span className="text-sm text-chocolate-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE ISSUE (dark) ─────────────────────────────────── */}
      <section className="bg-chocolate-900">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="grid md:grid-cols-3 gap-8 md:gap-10">

            {/* Left */}
            <div>
              <h2 className="text-3xl font-extrabold text-white leading-tight mb-6 tracking-tight">
                The issue is not your skill.<br />
                It is not your effort.<br />
                And it is not simply the market.
              </h2>
              <div className="rounded-xl p-5 mb-6 bg-chocolate-800 border border-chocolate-700">
                <div className="flex items-start gap-3">
                  <Lightbulb size={17} className="text-sage-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-chocolate-200 leading-relaxed">
                    The real issue is this: Most coaches try to apply advice, strategies,
                    and actions before identifying the condition they are actually operating from.
                  </p>
                </div>
              </div>
              <p className="text-sm font-semibold text-white">
                That is where good coaches get stuck.
              </p>
            </div>

            {/* Middle */}
            <div>
              <ul className="space-y-2.5 mb-6">
                {[
                  'They look for the next strategy.',
                  'The next certification.',
                  'The next marketing method.',
                  'The next mindset shift.',
                  'The next productivity system.',
                  'The next personal development breakthrough.',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-chocolate-300">
                    <span className="mt-2 w-1 h-1 rounded-full bg-chocolate-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-chocolate-300 leading-relaxed mb-4">
                But even the best advice produces the wrong result when it is applied from the wrong condition.
              </p>
              <p className="text-sm text-chocolate-300 leading-relaxed">
                If you are in one condition and applying the formula for another,
                more effort does not create true progress.
              </p>
            </div>

            {/* Right */}
            <div>
              <div className="space-y-4 mb-6">
                {[
                  'More marketing deepens the confusion.',
                  'More action creates more pressure.',
                  'More learning delays the real correction.',
                  'More motivation pushes you harder in the wrong direction.',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full border border-sage-600 flex items-center justify-center">
                      <RefreshCw size={10} className="text-sage-400" />
                    </span>
                    <span className="text-sm text-chocolate-200 leading-snug">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-chocolate-300 leading-relaxed mb-3">
                Sometimes the problem is not that you are doing too little.
              </p>
              <p className="text-sm font-semibold text-white leading-relaxed">
                The problem is that you are doing more before you have identified
                the actual condition underneath the plateau.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── DIFFERENT WAY (cream) ────────────────────────────── */}
      <section className="bg-cream-300">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="grid md:grid-cols-2 gap-8 md:gap-0">
            {/* Left */}
            <div className="md:pr-10 pb-8 md:pb-0 border-b md:border-b-0 md:border-r border-cream-500">
              <div className="w-14 h-14 rounded-full bg-cream-400 flex items-center justify-center text-3xl font-serif text-chocolate-500 mb-5 leading-none pt-1">
                "
              </div>
              <h2 className="text-2xl font-bold text-chocolate-900 mb-5">
                There is a different way to look at this.
              </h2>
              <p className="text-sm text-chocolate-700 mb-3">
                Instead of asking: "What else should I do?"
              </p>
              <p className="text-sm font-semibold text-sage-700">
                You first ask: "What condition am I actually operating from?"
              </p>
            </div>

            {/* Right */}
            <div className="md:pl-10">
              <p className="text-sm text-chocolate-700 leading-relaxed mb-5">
                Because once you identify the real condition, the next right
                action becomes much clearer.
              </p>
              <ul className="space-y-3">
                {[
                  'You stop guessing.',
                  'You stop pushing harder in the wrong direction.',
                  'You stop adding more on top of confusion.',
                  'And you start applying the correct formula for where you actually are.',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <GreenCheck />
                    <span className="text-sm text-chocolate-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── DISCOVER (cream) ─────────────────────────────────── */}
      <section className="bg-cream-200 border-t border-cream-400">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-xl font-bold text-chocolate-900 text-center mb-10">
            In this free 90-minute live training, you'll discover:
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: <Key size={20} />, text: 'The key conditions that determine what kind of action will actually work for you right now' },
              { icon: <Zap size={20} />, text: 'Why more effort, more marketing, and more personal development can make things worse — and what to do instead' },
              { icon: <Cloud size={20} />, text: 'The invisible ceiling even successful, high-performing coaches hit, and why motivation or "correct" strategies alone do not break it' },
              { icon: <RefreshCw size={20} />, text: 'Why some people create momentum, success, or income — and then lose it again' },
              { icon: <Clipboard size={20} />, text: 'A simple self-assessment you can use immediately to identify your current condition and your next right move' },
              { icon: <Target size={20} />, text: 'How to stop treating your business, income, and personal growth as one giant problem — and start handling the actual condition underneath it' },
            ].map(({ icon, text }) => (
              <div
                key={text}
                className="rounded-xl p-5 bg-cream-100 border border-cream-400"
              >
                <GreenIconCircle>{icon}</GreenIconCircle>
                <p className="mt-3 text-sm text-chocolate-700 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO THIS IS FOR (dark + plant bg) ────────────────── */}
      <section className="relative overflow-hidden bg-chocolate-900">
        {/* Plant background image — no text rendered over it */}
        <div className="absolute inset-0 pointer-events-none select-none">
          <img
            src="/plant_background_image.png"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-chocolate-900/85" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-14">
          <div className="grid md:grid-cols-2 gap-10">
            {/* Left */}
            <div>
              <GreenIconCircle>
                <Users size={22} />
              </GreenIconCircle>
              <h2 className="text-2xl font-bold text-white mt-4 mb-4">Who this is for</h2>
              <p className="text-sm text-chocolate-200 leading-relaxed">
                This training is for coaches, NLP practitioners, hypnotherapists,
                wellness professionals, consultants, and trainers who are doing
                meaningful work but feel plateaued in their own growth or business.
              </p>
            </div>

            {/* Right */}
            <div className="flex flex-col justify-center space-y-4">
              {[
                { text: 'It is not for complete beginners.', highlight: false },
                { text: 'It is not for people looking for a quick motivational fix.', highlight: false },
                { text: 'It is for practitioners who are ready to stop pushing harder and start applying the right formula.', highlight: true },
              ].map(({ text, highlight }) => (
                <div key={text} className="flex items-start gap-3">
                  <Star
                    size={15}
                    className="flex-shrink-0 mt-0.5 text-sage-500"
                    fill={highlight ? 'currentColor' : 'none'}
                  />
                  <span className={`text-sm leading-relaxed ${highlight ? 'text-white font-medium' : 'text-chocolate-300'}`}>
                    {text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── REGISTRATION FORM ────────────────────────────────── */}
      <section className="bg-chocolate-900 border-t border-chocolate-800" ref={formRef}>
        <div className="max-w-xl mx-auto px-6 py-14">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage-900/40 border border-sage-700/40 text-sage-400 text-xs font-semibold mb-4 tracking-wide uppercase">
              <Leaf size={12} />
              Free · Live · Interactive
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Save Your Free Seat</h2>
            <p className="text-sm text-chocolate-300">{EVENT_DATE} · 90 minutes + Q&amp;A</p>
          </div>
          <div className="rounded-xl overflow-hidden border border-chocolate-700">
            <GHLForm />
          </div>
        </div>
      </section>

      {/* ── STICKY FOOTER BAR ────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-chocolate-900 border-t border-chocolate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4">
          {/* Title block */}
          <div className="hidden sm:block min-w-0 flex-shrink-0 max-w-[220px]">
            <p className="text-white font-bold text-sm leading-tight">The Coaching Plateau</p>
            <p className="text-xs text-chocolate-400 mt-0.5 leading-tight">
              Why <span className="text-sage-400">Good Coaches Stay Stuck</span> — and What Actually Breaks Them Through
            </p>
          </div>

          {/* Event details */}
          <div className="hidden lg:flex items-center gap-5 text-xs text-chocolate-400 flex-1 justify-center">
            <span className="flex items-center gap-1.5">
              <Calendar size={12} className="text-sage-500" />
              Thursday, 9 July 2026
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={12} className="text-sage-500" />
              10:00 AM – 11:30 AM Eastern
            </span>
            <span className="flex items-center gap-1.5">
              <Globe size={12} className="text-sage-500" />
              3:00 PM – 4:30 PM UK &amp; Europe
            </span>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <button
              onClick={scrollToForm}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-sage-700 hover:bg-sage-800 text-white font-semibold text-sm transition-colors"
            >
              Save My Free Seat →
            </button>
            <p className="text-xs text-chocolate-500">100% Free · Live · Interactive · Limited Seats</p>
          </div>
        </div>
      </div>

    </div>
  );
}
